import { useState } from "react";
import { Stage, StageTabs, Inp, ExpandTable, BlockHead, Detalhe } from "../components/primitives";

const AGENTS = [
  {
    id: "analytics",
    nome: "Analytics Agent",
    tipo: "Operacional",
    icone: "◈",
    descricao: "Analisa o funil completo do clique ao paciente que pagou. Transforma dados de campanhas em diagnóstico de onde o dinheiro está sendo perdido.",
    quando: [
      "Depois de um mês com leads altos mas faturamento baixo",
      "Quando o cliente questiona o resultado das campanhas",
      "Para mapear onde leads somem entre agendamento e consulta",
      "Antes de reunião mensal de performance",
    ],
    porque: "A maioria das agências reporta CPL. O Analytics Agent calcula o CPP (Custo por Paciente Real) — a única métrica que o cliente entende como resultado.",
    como: "Use o analytics agent para analisar o funil do [cliente]",
    entrega: "Mapa de abandono + 3 ações prioritárias",
    cor: "var(--cat-violet)",
  },
  {
    id: "traffic",
    nome: "Traffic Agent",
    tipo: "Operacional",
    icone: "⟁",
    descricao: "Audita campanhas Meta Ads em busca de sangrias de verba. Identifica conjuntos, públicos e criativos que consomem orçamento sem retorno.",
    quando: [
      "CPL subiu mais de 20% em relação ao mês anterior",
      "ROAS caiu sem alteração de criativos",
      "Cliente reclamou que 'os anúncios estão caros'",
      "Ao assumir uma conta nova para diagnóstico inicial",
    ],
    porque: "Sangrias silenciosas destroem contas. Um conjunto com frequência 8+ ou um público sobreposto pode estar desperdiçando 30% do orçamento sem alarme visível.",
    como: "Use o traffic agent para auditar as campanhas de [cliente] em [período]",
    entrega: "Top 5 sangrias identificadas + plano de ação semanal",
    cor: "var(--danger)",
  },
  {
    id: "crm",
    nome: "CRM Agent",
    tipo: "Operacional",
    icone: "◎",
    descricao: "Monitora a saúde da carteira de clientes da agência. Identifica risco de churn, oportunidades de upsell e clientes que precisam de atenção.",
    quando: [
      "Início de mês para revisar a carteira",
      "Quando um cliente está há mais de 15 dias sem contato",
      "Para identificar quem está próximo do vencimento de contrato",
      "Antes de reunião de planejamento comercial",
    ],
    porque: "Manter cliente custa 5x menos que adquirir um novo. O CRM Agent detecta sinais de insatisfação antes que virem cancelamento.",
    como: "Use o crm agent para analisar a saúde da carteira",
    entrega: "Dashboard de risco por cliente + alertas de ação",
    cor: "var(--warn)",
  },
  {
    id: "social",
    nome: "Social Agent",
    tipo: "Operacional",
    icone: "◻",
    descricao: "Cria conteúdo para Instagram da RC2Clicks e de clientes médicos. Gera roteiros de Reels, captions, carrosséis e briefings visuais no padrão da marca.",
    quando: [
      "Para criar o planejamento semanal de posts",
      "Quando precisa de roteiro para Reel de resultado",
      "Para criar carrossel educativo sobre um tratamento",
      "Briefing de criativo para o Canva",
    ],
    porque: "Conteúdo consistente é o que transforma autoridade em leads orgânicos. O Social Agent já conhece as regras CFM e o tom da marca — não precisa explicar do zero.",
    como: "Use o social agent para criar [tipo de conteúdo] para [agência/cliente]",
    entrega: "Roteiro completo + briefing visual para Canva",
    cor: "var(--success)",
  },
  {
    id: "strategy",
    nome: "Strategy Agent",
    tipo: "Operacional",
    icone: "⬡",
    descricao: "Analisa posicionamento competitivo, oportunidades de mercado e estratégia de campanha. Cruza dados de mercado com contexto do cliente.",
    quando: [
      "Antes de iniciar campanhas para um nicho novo",
      "Quando o cliente pergunta 'o que os concorrentes estão fazendo'",
      "Para definir o ângulo de mensagem de uma nova campanha",
      "Review trimestral de posicionamento",
    ],
    porque: "Campanha sem estratégia é verba queimada com mais estrutura. O Strategy Agent garante que a tática serve o posicionamento — não o contrário.",
    como: "Use o strategy agent para analisar o posicionamento de [cliente] no nicho [especialidade]",
    entrega: "Análise competitiva + 3 recomendações estratégicas",
    cor: "var(--cat-purple)",
  },
  {
    id: "copy-medico",
    nome: "Copy Médico",
    tipo: "Operacional",
    icone: "✦",
    descricao: "Gera copy para anúncios, landing pages e conteúdo de clientes médicos. Aplica automaticamente as restrições do CFM em todo texto gerado.",
    quando: [
      "Para criar headlines de anúncios de clínicas",
      "Caption de post para Instagram de médico",
      "Texto de landing page de procedimento",
      "Copy de Reel paciente-facing",
    ],
    porque: "Copy médico errado é multa do CFM. O agente já tem todas as regras internalizadas — nunca usa 'garantia', 'cura', 'melhor', 'resultado certo' sem contexto adequado.",
    como: "Use o copy agent médico para criar [tipo de copy] para [especialidade/procedimento]",
    entrega: "Headlines + caption CFM-compliant prontos para publicar",
    cor: "var(--info)",
  },
  {
    id: "copy-agencia",
    nome: "Copy Agência",
    tipo: "Operacional",
    icone: "◈",
    descricao: "Cria copy B2B para posicionamento da RC2Clicks. Usa a narrativa de transparência radical (CPL vs CPP) e os números reais da agência.",
    quando: [
      "Para criar post de resultado da RC2Clicks",
      "Roteiro de Reel de posicionamento",
      "Copy de proposta comercial",
      "Texto de anúncio prospectando médicos",
    ],
    porque: "O posicionamento da RC2Clicks é único — anti-agência, transparência radical, CPP ao invés de CPL. O Copy Agência já conhece essa narrativa e os números reais (R$83,47 CPL médio).",
    como: "Use o copy agent agência para criar [tipo de material] com foco em [objetivo]",
    entrega: "Roteiro/caption B2B no tom RC2Clicks + variações",
    cor: "var(--signature)",
  },
];

const SQUADS = [
  {
    id: "product-master",
    nome: "Product Master",
    icone: "①",
    descricao: "Define prioridades de produto, critérios de 'done' e backlog. Decide o que construir e em que ordem.",
    quando: ["Antes de iniciar uma nova feature", "Para priorizar entre 3 tarefas concorrentes", "Review de backlog semanal"],
    como: "Squad: Product Master. Tarefa: [descreva a decisão ou priorização]",
    entrega: "Decisão + PRÓXIMO PASSO claro",
  },
  {
    id: "system-architect",
    nome: "System Architect",
    icone: "②",
    descricao: "Define schema Supabase, RLS, APIs e arquitetura de dados. Entrega SQL completo e executável.",
    quando: ["Nova tabela ou relação no banco", "Configurar RLS para novo tenant", "Definir estrutura de API"],
    como: "Squad: System Architect. Tarefa: [descreva a necessidade de schema ou API]",
    entrega: "SQL completo executável + comentários de RLS",
  },
  {
    id: "frontend-master",
    nome: "Frontend Master",
    icone: "③",
    descricao: "Desenvolve componentes Next.js, ShadCN e React. Entrega código completo com imports.",
    quando: ["Nova tela ou componente", "Refatoração de componente existente", "Integração de UI com dados"],
    como: "Squad: Frontend Master. Tarefa: [descreva a tela ou componente]",
    entrega: "Código completo com imports prontos para colar",
  },
  {
    id: "automation-master",
    nome: "Automation Master",
    icone: "④",
    descricao: "Cria fluxos N8N, WhatsApp e gatilhos de automação. Entrega pseudocódigo + JSON exportável para N8N.",
    quando: ["Nova automação de recorrência", "Integração WhatsApp com evento do sistema", "Novo gatilho de notificação"],
    como: "Squad: Automation Master. Tarefa: [descreva o fluxo de automação]",
    entrega: "Pseudocódigo do fluxo + JSON N8N exportável",
  },
  {
    id: "ai-master",
    nome: "AI Master",
    icone: "⑤",
    descricao: "Integra OpenAI, cria prompts de classificação e IA aplicada ao produto. Pós-MVP.",
    quando: ["Classificação automática de pacientes", "Geração de sugestões baseadas em histórico", "Análise de sentimento em texto"],
    como: "Squad: AI Master. Tarefa: [descreva a necessidade de IA]",
    entrega: "Prompt testável + estimativa de custo por chamada",
  },
  {
    id: "ux-operations",
    nome: "UX Operations",
    icone: "⑥",
    descricao: "Projeta fluxos de tela, reduz cliques e garante mobile-first. Entrega wireframe textual.",
    quando: ["Fluxo novo que envolve múltiplas telas", "Reduzir fricção em tarefa frequente", "Validar hierarquia de ação numa tela"],
    como: "Squad: UX Operations. Tarefa: [descreva o fluxo ou tela]",
    entrega: "Wireframe textual com anotações de UX",
  },
  {
    id: "qa-master",
    nome: "QA Master",
    icone: "⑦",
    descricao: "Define testes, edge cases e critérios de validação. Garante que o que foi construído realmente funciona.",
    quando: ["Antes de deploy de feature crítica", "Definir edge cases de automação", "Criar checklist de smoke test"],
    como: "Squad: QA Master. Tarefa: [descreva a feature a ser testada]",
    entrega: "Tabela de cenário/resultado + edge cases",
  },
  {
    id: "visual-designer",
    nome: "Visual Designer",
    icone: "⑧",
    descricao: "Cria briefings visuais, identidade e direção criativa para Canva. Conhece o Brand System da RC2Clicks.",
    quando: ["Briefing de criativo para Canva", "Template de apresentação", "Direção visual de nova seção"],
    como: "Squad: Visual Designer. Tarefa: [descreva o material visual]",
    entrega: "Briefing visual completo com referências e especificações",
  },
  {
    id: "devops",
    nome: "DevOps",
    icone: "⑨",
    descricao: "Deploy, CI/CD, configuração Vercel e infraestrutura. Entrega configs e comandos prontos.",
    quando: ["Novo deploy de produto", "Configurar variáveis de ambiente", "Troubleshooting de build"],
    como: "Squad: DevOps. Tarefa: [descreva o problema ou configuração]",
    entrega: "Configuração + comandos para executar",
  },
  {
    id: "integrations",
    nome: "Integrations",
    icone: "⑩",
    descricao: "Integra APIs externas, webhooks e Evolution API. Garante que sistemas externos se comunicam corretamente.",
    quando: ["Nova integração com API externa", "Webhook recebendo dados inesperados", "Evolution API com problema de instância"],
    como: "Squad: Integrations. Tarefa: [descreva a integração ou problema]",
    entrega: "Código de integração + tratamento de erro",
  },
  {
    id: "n8n-master",
    nome: "N8N Master",
    icone: "⑪",
    descricao: "Arquitetura avançada de N8N e troubleshooting. Para quando o Automation Master não resolve.",
    quando: ["Workflow complexo com múltiplas condições", "Bug em automação existente", "Otimizar workflow com muitos nós"],
    como: "Squad: N8N Master. Tarefa: [descreva o workflow ou problema]",
    entrega: "JSON N8N + pseudocódigo explicando a lógica",
  },
];

const TABS = ["Todos", "Operacionais", "Squads Dev", "Criação Visual"];

const BRIEFING_ITEMS = [
  { label: "Para quem?", detalhe: "RC2CLICKS própria (posicionamento da agência) ou para um cliente?" },
  { label: "Nicho / especialidade?", detalhe: "Ortopedia, estética, e-commerce, odontologia..." },
  { label: "Objetivo do post?", detalhe: "Posicionamento, captação de lead, educativo, provocação, antes/depois" },
  { label: "Plataforma?", detalhe: "Instagram feed, Stories, Reels, LinkedIn" },
  { label: "Dados reais disponíveis?", detalhe: "CPL, leads, ROAS, período — nunca inventar números" },
  { label: "Cliente médico? (CFM)", detalhe: "Se sim, checklist CFM é ativado automaticamente em todo copy" },
  { label: "Tom?", detalhe: "Frio/analítico, emocional, provocativo, educativo" },
];

const SISTEMA_DOCS = [
  {
    etapa: "01",
    titulo: "Direção Visual",
    arquivo: "social-visual-direction.md",
    descricao: "Estética operacional da marca. O que torna um post RC2CLICKS, elementos obrigatórios, 3 conceitos visuais, feed grid.",
    uso: "Leia antes de definir o conceito criativo do post",
    cor: "var(--cat-violet)",
  },
  {
    etapa: "02",
    titulo: "Sistema de Execução",
    arquivo: "social-execution-system.md",
    descricao: "Blueprints pixel-level: margens, espaçamento, linha dourada, hierarquia tipográfica, anatomia do dado, CTAs, 5 templates.",
    uso: "Leia antes de descrever a composição visual de cada slide",
    cor: "var(--signature)",
  },
  {
    etapa: "03",
    titulo: "Templates Canva",
    arquivo: "canva-templates-guide.md",
    descricao: "Coordenadas X/Y/W/H para cada elemento, ordem de camadas, Brand Kit, configuração de guias, exportação.",
    uso: "Abra no Canva e siga slide a slide para o resultado pixel-perfeito",
    cor: "var(--success)",
  },
];

const FLUXO_ETAPAS = [
  {
    num: "1",
    titulo: "Briefing",
    desc: "Solicite o conteúdo informando os 7 dados do briefing. O Social Agent ou Visual Designer não deve criar sem essas informações.",
    exemplo: `"Crie um carrossel para a RC2CLICKS, posicionamento da agência, tema: custo real por paciente, analítico, dados: CPL R$83,47 vs CPP R$432,18, 30 dias, clínica médica"`,
  },
  {
    num: "2",
    titulo: "Estrutura Operacional",
    desc: "O agente gera o spec completo: headline por slide, microcopy, hierarquia visual, coordenadas para o Canva.",
    exemplo: "Arquivo salvo em COPIES/ com nome kebab-case",
  },
  {
    num: "3",
    titulo: "Canva — Geração IA",
    desc: "Use o MCP do Canva no Claude Code para gerar 4 versões candidatas. Escolha a mais próxima do brand.",
    exemplo: `"Gere o carrossel no Canva seguindo o spec em COPIES/[arquivo].md"`,
  },
  {
    num: "4",
    titulo: "Canva — Refinamento",
    desc: "Redimensione para 1080×1080px. Ajuste textos, cores e fontes seguindo o canva-templates-guide.md.",
    exemplo: "Exportar como PNG, qualidade máxima, 1 arquivo por slide",
  },
  {
    num: "5",
    titulo: "Publicação",
    desc: "Postar no Instagram como carrossel. Primeiro slide deve funcionar como post autônomo.",
    exemplo: "Legenda no Google Drive ou COPIES/ antes de publicar",
  },
];

const MONO = {
  background: "var(--surface)", borderRadius: 12, padding: "8px 14px",
  fontFamily: "var(--font-data)", fontSize: 12, color: "var(--ink)", lineHeight: 1.5,
  border: "1px solid var(--line)",
};

/** Agentes: uma linha por agente; quando usar, por que e como invocar ao abrir. */
function AgentTable({ agents, isSquad }) {
  return (
    <ExpandTable
      rows={agents}
      minWidth={560}
      empty="Nenhum agente encontrado."
      columns={[
        { key: "nome", label: "Agente", render: (a) => (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span aria-hidden="true" style={{ color: isSquad ? "var(--muted)" : a.cor, width: 18, textAlign: "center" }}>{a.icone}</span>
            <span className="name">{a.nome}</span>
          </span>
        ) },
        { key: "entrega", label: "Entrega", render: (a) => <span className="sub">{a.entrega}</span> },
      ]}
      detail={(a) => (
        <div className="detail-grid">
          <Detalhe label="O que faz">{a.descricao}</Detalhe>
          <div className="detalhe">
            <span className="eyebrow">Quando usar</span>
            <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 2 }}>
              {a.quando.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
          {!isSquad && <Detalhe label="Por que usar">{a.porque}</Detalhe>}
          <div className="detalhe"><span className="eyebrow">Como invocar no Claude Code</span><div style={MONO}>"{a.como}"</div></div>
        </div>
      )}
    />
  );
}

export function BibliotecaView() {
  const [tab, setTab] = useState("Todos");
  const [search, setSearch] = useState("");

  const q = search.toLowerCase();
  const matchesSearch = (a) => !q || a.nome.toLowerCase().includes(q) || a.descricao.toLowerCase().includes(q);
  const agentes = AGENTS.filter(matchesSearch);
  const squads  = SQUADS.filter(matchesSearch);
  const total   = tab === "Operacionais" ? agentes.length : tab === "Squads Dev" ? squads.length : agentes.length + squads.length;

  return (
    <div className="view" style={{ maxWidth: 1040 }}>

      {/* Palco */}
      <Stage
        path="como-usar"
        title="Como usar os"
        hl="agentes"
        lead="Os agentes de IA da RC2Clicks: o que fazem, quando acionar e como invocar."
        tabs={<StageTabs label="Seções da biblioteca" items={TABS.map((t) => ({ id: t, label: t }))} active={tab} onChange={setTab} />}
      />

      {tab !== "Criação Visual" && (
        <div className="tab-bar">
          <Inp value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar agente..." style={{ maxWidth: 320 }} />
          <span className="eyebrow">{total} {total === 1 ? "agente" : "agentes"}</span>
        </div>
      )}

      {(tab === "Todos" || tab === "Operacionais") && (
        <>
          <BlockHead title="Agentes operacionais" sub={`${agentes.length} no dia a dia dos clientes`} />
          <AgentTable agents={agentes} isSquad={false} />
        </>
      )}

      {(tab === "Todos" || tab === "Squads Dev") && (
        <>
          <BlockHead title="Squads de desenvolvimento" sub={`${squads.length} para construir produto`} />
          <AgentTable agents={squads} isSquad />
        </>
      )}

      {/* Aba Criação Visual */}
      {tab === "Criação Visual" && (
        <>
          <BlockHead title="Briefing obrigatório" sub="7 perguntas antes de criar. O agente pergunta o que faltar no pedido." />
          <div className="klist">
            {BRIEFING_ITEMS.map((item, i) => (
              <div key={i} className="klist-row">
                <span className="minibar-n" style={{ width: 18 }}>{i + 1}</span>
                <span className="txt"><strong style={{ fontWeight: 600 }}>{item.label}</strong><span style={{ color: "var(--muted)" }}> · {item.detalhe}</span></span>
              </div>
            ))}
          </div>

          <BlockHead title="Sistema visual RC2Clicks" sub="3 documentos de referência em RC2CLICKS-CORE/DESIGN-SYSTEM/brand/" />
          <ExpandTable
            rows={SISTEMA_DOCS}
            rowKey={(d) => d.etapa}
            minWidth={480}
            columns={[
              { key: "etapa", label: "#", render: (d) => <span className="eyebrow">{d.etapa}</span> },
              { key: "titulo", label: "Documento", render: (d) => <span className="name">{d.titulo}</span> },
              { key: "arquivo", label: "Arquivo", render: (d) => <code style={{ fontFamily: "var(--font-data)", fontSize: 12, color: "var(--muted)" }}>{d.arquivo}</code> },
            ]}
            detail={(d) => (
              <div className="detail-grid">
                <Detalhe label="O que é">{d.descricao}</Detalhe>
                <Detalhe label="Quando usar">{d.uso}</Detalhe>
              </div>
            )}
          />

          <BlockHead title="Fluxo de execução" sub="Do briefing ao post publicado." />
          <ExpandTable
            rows={FLUXO_ETAPAS}
            rowKey={(e) => e.num}
            minWidth={480}
            columns={[
              { key: "num", label: "#", render: (e) => <span className="eyebrow">{e.num}</span> },
              { key: "titulo", label: "Etapa", render: (e) => <span className="name">{e.titulo}</span> },
            ]}
            detail={(e) => (
              <div className="detail-grid">
                <Detalhe label="O que fazer">{e.desc}</Detalhe>
                <div className="detalhe"><span className="eyebrow">Exemplo</span><div style={MONO}>{e.exemplo}</div></div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
